import React, {
  FC,
  ReactNode,
  useContext,
  useMemo,
  useState,
  createContext,
} from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { defineMessages, useIntl } from "react-intl";

import { TransactionStatus } from "@natera/platform/lib/service/session/okta";
import { routes } from "@app/routing";
import {
  EnrollFactorResponse,
  FACTORS_TO_SHOW,
  IDP_TYPE,
} from "@app/service/user";
import {
  ConfigContext,
  NotificationContext,
  ServiceContext,
  SignUpContext,
  UserContext,
  UppAuthContext,
} from "@app/provider";
import { LoginStatus } from "@app/provider/user";
import { Credentials } from "@app/service/session";
import {
  useOtpState,
  usePortalRedirects,
  useTokenSourceFromQuery,
} from "@app/hooks";
import {
  clearOktaAuthStorage,
  createOtpOktaAuth,
  setOtpVerificationModeStorage,
  startOtpIdxFlow,
} from "@app/utils";
import { useActivationModeContextOptional } from "@app/provider/activationMode";
import R from "ramda";

const messages = defineMessages({
  signInForgotPassword: {
    id: "signInForgotPassword",
    defaultMessage: "Forgot password?",
  },
  signInIncorrectCredentials: {
    id: "signInIncorrectCredentials",
    defaultMessage: "Incorrect email or password",
  },
  signInInactiveAccountLoginError: {
    id: "signInInactiveAccountLoginError",
    defaultMessage: "We're sorry. Something went wrong.",
  },
  signInAttemptsRemain: {
    id: "signInAttemptsRemain",
    defaultMessage: "Attempts Remaining — {attempts}",
  },
  signInMaxAttemptsText: {
    id: "signInMaxAttemptsText",
    defaultMessage:
      "After 5 consecutive unsuccessful login attempts, your account will be locked",
  },
  signInTryAgainAccountActivated: {
    id: "signInTryAgainAccountActivated",
    defaultMessage:
      "Please try again using a different email and password combination.",
  },
  signInUnlockAccount: {
    id: "signInUnlockAccount",
    defaultMessage: "Unlock Account",
  },
  signInAccountLocked: {
    id: "signInAccountLocked",
    defaultMessage: "Account Locked",
  },
  signInAccountLockedText: {
    id: "signInAccountLockedText",
    defaultMessage:
      "Your account has been locked due to multiple incorrect attempts.",
  },
});

export enum OktaErrorCodes {
  AUTH_ERROR = "E0000004",
}

export enum LoginMode {
  OTP = "otp",
  PASSWORD = "password",
}

export const ATTEMPTS_COUNT_TO_SHOW = 2;

interface ErrorResponse {
  errorSummary: string;
  errorCode: string;
  status?: string;
}

export const getFilteredFactors = (
  factors?: EnrollFactorResponse[]
): EnrollFactorResponse[] | undefined => {
  if (!factors) return;
  const filteredFactorList = R.filter(
    (factor) => factor.factorType in FACTORS_TO_SHOW,
    factors
  );
  return filteredFactorList;
};

export interface SignInContextValue {
  loginMode: LoginMode;
  setLoginMode: (mode: LoginMode) => void;
  switchLoginMode: () => void;

  // Password login
  handlePasswordSubmit: (props: Credentials) => Promise<void>;
  isLoginPending: boolean;

  // OTP login
  handleOtpRequest: (
    email: string,
    options?: { fromSignUpFlow?: boolean }
  ) => Promise<void>;
  isOtpRequestPending: boolean;
  otpErrorMessage: string | undefined;

  // Factors
  factors: EnrollFactorResponse[] | undefined;
  setFactors: React.Dispatch<
    React.SetStateAction<EnrollFactorResponse[] | undefined>
  >;
  factor: EnrollFactorResponse | undefined;
  setFactor: React.Dispatch<
    React.SetStateAction<EnrollFactorResponse | undefined>
  >;
  showFactorSelection: boolean;
  setShowFactorSelection: React.Dispatch<React.SetStateAction<boolean>>;
}

export const SignInContext = createContext<SignInContextValue | undefined>(
  undefined
);

export const useSignInContext = (): SignInContextValue => {
  const context = useContext(SignInContext);
  if (!context) {
    throw new Error("useSignInContext must be used within SignInProvider");
  }
  return context;
};

interface SignInProviderProps {
  children: ReactNode;
}

const SignInProvider: FC<SignInProviderProps> = ({ children }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const { toLoginRedirect } = usePortalRedirects();
  const {
    loadProfile,
    logUserLogin,
    addUserToPasswordlessGroup,
    removeUserFromPasswordlessGroup,
    handleUserLogin,
  } = useContext(UserContext);
  const { checkUserExisted, setSignUpData } = useContext(SignUpContext);
  const { config } = useContext(ConfigContext);
  const { addNotification, clear } = useContext(NotificationContext);
  const { sessionService } = useContext(ServiceContext);
  const { setActivationEmail } = useContext(UppAuthContext);

  const { token } = useTokenSourceFromQuery();
  const activationMode = useActivationModeContextOptional();

  const isOtpVerificationEnabled = config.otpVerification?.enabled;

  const [loginMode, setLoginMode] = useState<LoginMode>(
    isOtpVerificationEnabled ? LoginMode.OTP : LoginMode.PASSWORD
  );
  const [isLoginPending, setIsLoginPending] = useState(false);
  const {
    isOtpRequestPending,
    setIsOtpRequestPending,
    otpErrorMessage,
    setOtpTransaction,
    bumpOtpResendKey,
    storeOtpEmail,
    storeOtpStateHandle,
    clearOtpState,
    clearOtpError,
  } = useOtpState();

  const [showFactorSelection, setShowFactorSelection] =
    useState<boolean>(false);
  const [factors, setFactors] = useState<EnrollFactorResponse[]>();
  const [factor, setFactor] = useState<EnrollFactorResponse>();

  const oktaAuth = useMemo(() => {
    return createOtpOktaAuth(config.okta);
  }, [config.okta]);

  const getResetPasswordLink = () => (
    <Link to={routes.resetPassword} onClick={() => clear()}>
      {intl.formatMessage(messages.signInForgotPassword)}
    </Link>
  );

  const addNotificationInactiveAccountLoginFailure = () => {
    addNotification({
      type: "error",
      message: intl.formatMessage(messages.signInInactiveAccountLoginError),
    });
  };

  const addNotificationAttemptsRemaining = (
    attemptsLeft?: number,
    isAccountActivated?: boolean
  ) => {
    if (!isAccountActivated) {
      addNotificationInactiveAccountLoginFailure();
      return;
    }

    addNotification({
      title: intl.formatMessage(messages.signInIncorrectCredentials),
      message: (
        <>
          <div>
            {intl.formatMessage(messages.signInAttemptsRemain, {
              attempts: attemptsLeft,
            })}
          </div>
          <div>{intl.formatMessage(messages.signInMaxAttemptsText)}</div>
        </>
      ),
      actions: getResetPasswordLink(),
      type: "warning",
    });
  };

  const addNotificationTryAgain = (isAccountActivated?: boolean) => {
    if (!isAccountActivated) {
      addNotificationInactiveAccountLoginFailure();
      return;
    }

    addNotification({
      title: intl.formatMessage(messages.signInIncorrectCredentials),
      message: intl.formatMessage(messages.signInTryAgainAccountActivated),
      actions: getResetPasswordLink(),
      type: "warning",
    });
  };

  const handleLogUserLogin = async (email: string) => {
    try {
      const data = await logUserLogin({
        email,
        status: LoginStatus.FAIL,
        token,
      });

      if (!data) {
        throw new Error();
      }

      const { attemptsLeft, isAccountActivated } = data;

      if (attemptsLeft && attemptsLeft <= ATTEMPTS_COUNT_TO_SHOW) {
        addNotificationAttemptsRemaining(attemptsLeft, isAccountActivated);
      } else {
        addNotificationTryAgain(isAccountActivated);
      }
    } catch (e) {
      addNotification({
        title: intl.formatMessage(messages.signInIncorrectCredentials),
        type: "warning",
      });
    }
  };

  const handleError = async (err: ErrorResponse, email: string) => {
    switch (err.errorCode) {
      case OktaErrorCodes.AUTH_ERROR:
        await handleLogUserLogin(email);
        break;
      default:
        if (err.status === TransactionStatus.LOCKED_OUT) {
          const actions = (
            <Link to={routes.unlock} state={email}>
              {intl.formatMessage(messages.signInUnlockAccount)}
            </Link>
          );
          addNotification({
            title: intl.formatMessage(messages.signInAccountLocked),
            message: intl.formatMessage(messages.signInAccountLockedText),
            actions,
            type: "error",
          });
        } else {
          addNotification({
            type: "error",
          });
        }
        break;
    }
  };

  const startOtpFlow = async (email: string): Promise<boolean> => {
    setIsOtpRequestPending(true);
    clearOtpError();
    try {
      const { transaction, stateHandle } = await startOtpIdxFlow(
        oktaAuth,
        email
      );
      setOtpTransaction(transaction);
      storeOtpStateHandle(stateHandle);
      return true;
    } catch {
      return false;
    } finally {
      setIsOtpRequestPending(false);
    }
  };

  const handleOtpRequest = async (
    email: string,
    options?: { fromSignUpFlow?: boolean }
  ) => {
    if (!options?.fromSignUpFlow) {
      setSignUpData(null);
    }
    clearOtpState();
    storeOtpEmail(email);
    setActivationEmail(email);
    bumpOtpResendKey();
    clearOtpError();
    clear();
    if (!options?.fromSignUpFlow) {
      await sessionService.clearToken();
    }
    clearOktaAuthStorage();
    if (!options?.fromSignUpFlow) {
      await addUserToPasswordlessGroup(email);
    }
    await startOtpFlow(email);
    // Send to the verification page even if an error occurs in the Okta
    setOtpVerificationModeStorage("signIn");
    activationMode?.setMode("signIn");
    const searchParams = new URLSearchParams(location.search);
    searchParams.delete("token");
    const cleanSearch = searchParams.toString();
    navigate({
      pathname: routes.activation,
      search: cleanSearch ? `?${cleanSearch}` : "",
    });
  };

  const handlePasswordSubmit = async (props: Credentials) => {
    setIsLoginPending(true);
    clear();
    try {
      if (await sessionService.getToken()) {
        await sessionService.clearToken();
      }

      const isUserExist = await checkUserExisted(props.email);
      isOtpVerificationEnabled &&
        (await removeUserFromPasswordlessGroup(props.email));

      if (
        isUserExist?.isExisted &&
        isUserExist.idpType &&
        isUserExist.idpType !== IDP_TYPE.EMAIL
      ) {
        toLoginRedirect({
          params: {
            provider: isUserExist.idpType.toUpperCase() as IDP_TYPE,
            email: props.email,
            ...(token ? { token } : {}),
          },
          preserveQuery: false,
        });
        return;
      }

      const transaction = await sessionService.login(props);
      const status = transaction.transaction.status;
      if (status === TransactionStatus.LOCKED_OUT) {
        throw { status };
      }
      await logUserLogin({
        email: props.email,
        status: LoginStatus.SUCCESS,
        token,
      });
      const factors = transaction.transaction.factors as EnrollFactorResponse[];
      const filteredFactors = getFilteredFactors(factors);
      if (filteredFactors && filteredFactors?.length > 0) {
        setFactors(filteredFactors);
        // Auto-select factor if only one, otherwise show selection
        if (filteredFactors.length === 1) {
          setFactor(filteredFactors[0]);
        } else {
          setShowFactorSelection(true);
        }
      } else {
        await handleUserLogin();
        await loadProfile();
      }
    } catch (error) {
      await handleError(error, props.email);
    } finally {
      setIsLoginPending(false);
    }
  };

  const switchLoginMode = () => {
    setLoginMode((prev) =>
      prev === LoginMode.OTP ? LoginMode.PASSWORD : LoginMode.OTP
    );
  };

  const value = useMemo<SignInContextValue>(
    () => ({
      loginMode,
      setLoginMode,
      switchLoginMode,
      handlePasswordSubmit,
      isLoginPending,
      handleOtpRequest,
      isOtpRequestPending,
      otpErrorMessage,
      factors,
      setFactors,
      factor,
      setFactor,
      showFactorSelection,
      setShowFactorSelection,
    }),
    [
      loginMode,
      setLoginMode,
      switchLoginMode,
      handlePasswordSubmit,
      isLoginPending,
      handleOtpRequest,
      isOtpRequestPending,
      otpErrorMessage,
      factors,
      setFactors,
      factor,
      setFactor,
      showFactorSelection,
      setShowFactorSelection,
    ]
  );

  return (
    <SignInContext.Provider value={value}>{children}</SignInContext.Provider>
  );
};

export default SignInProvider;
